import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { useTranslation } from 'react-i18next';

import { Margin } from 'styled-components-spacing';

import { Row, Col, Heading } from 'components/';
import Container, { Action } from './Modal.styled';
import { NoteField, NoteLabel } from './NoteModal.styled';

ReactModal.setAppElement('#modals');

/**
 * Confirmation modal that, unlike the plain Modal, can collect an optional
 * free-text reason from the user before confirming.
 *
 * The note is kept in this component's own local state and the textarea is part
 * of its stable JSX (not passed through Modal's `customContent`, which gets a
 * brand-new function identity on every render and would remount the input,
 * dropping focus on each keystroke). Typing therefore only re-renders this
 * component and never the parent, so the input keeps focus.
 */
function NoteModal({
  isOpen,
  heading,
  description,
  showNote,
  noteLabel,
  notePlaceholder,
  confirm,
  cancel,
  disabled,
  ...res
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');

  // Clear any previously typed reason whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setNote('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (disabled) return;
    const trimmed = note.trim();
    confirm(trimmed !== '' ? trimmed : null);
  };

  return (
    <ReactModal
      isOpen={!!isOpen}
      onRequestClose={cancel}
      style={{
        content: {
          maxWidth: 600,
          maxHeight: '100%',
          width: '100%',
          top: '50%',
          bottom: 'initial',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: 0,
          border: 'none',
          boxShadow: '0px 0px 48px 0px rgba(61,62,64,0.24)',
          overflow: 'auto',
        },
        overlay: {
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          zIndex: 40,
        },
      }}
      {...res}
    >
      <Container>
        <Row>
          <Col size={{ xs: 1 / 1 }}>
            <Heading as="h4" noLine>
              {heading}
            </Heading>
            {description && (
              <Margin top={{ xs: 24 }}>
                <p>{description}</p>
              </Margin>
            )}
          </Col>
        </Row>
        {showNote && (
          <Margin top={{ xs: 24 }}>
            <Row>
              <Col size={{ xs: 1 / 1 }}>
                <NoteLabel htmlFor="note-modal-reason">{noteLabel || t('global.reason')}</NoteLabel>
                <NoteField
                  id="note-modal-reason"
                  rows={4}
                  value={note}
                  placeholder={notePlaceholder}
                  onChange={(event) => setNote(event.target.value)}
                  disabled={disabled}
                />
              </Col>
            </Row>
          </Margin>
        )}
        <Margin top={{ xs: 32 }}>
          <Row>
            <Col size={{ xs: 1 / 2 }}>
              <Action onClick={handleConfirm} disabled={disabled}>
                {t('global.confirmButton')}
              </Action>
            </Col>
            <Col size={{ xs: 1 / 2 }}>
              <Action onClick={cancel} bordered disabled={disabled}>
                {t('global.cancelButton')}
              </Action>
            </Col>
          </Row>
        </Margin>
      </Container>
    </ReactModal>
  );
}

NoteModal.propTypes = {
  isOpen: PropTypes.bool,
  heading: PropTypes.string,
  description: PropTypes.node,
  showNote: PropTypes.bool,
  noteLabel: PropTypes.string,
  notePlaceholder: PropTypes.string,
  confirm: PropTypes.func,
  cancel: PropTypes.func,
  disabled: PropTypes.bool,
};

NoteModal.defaultProps = {
  isOpen: false,
  heading: null,
  description: null,
  showNote: false,
  noteLabel: null,
  notePlaceholder: null,
  confirm: () => {},
  cancel: () => {},
  disabled: false,
};

export default NoteModal;
